import { z } from 'zod';
import type { ProviderConfig } from '@servicetitan/financing-workflow-shared';
import { createApplicantSchema } from '../applicant/schema';
import { mailingAddressBaseSchema } from '../mailing-address/schema';
import { spouseInfoSchema, validateSimpleMarriageFields } from '../spouseInfo';

const noCoApplicant = z.object({
    hasCoApplicant: z.literal(false),
});

const spouseFieldsDetailed = {
    isMarried: z.boolean().optional(),
    spouseContact: z.string().optional(),
    spouseInfo: spouseInfoSchema.optional(),
};

const spouseFieldsSimple = {
    isMarried: z.boolean().optional(),
    spouseFirstName: z.string().optional(),
    spouseLastName: z.string().optional(),
    spouseContact: z.string().optional(),
};

const createWithMailingAddress = (hasDetailedMarriageFields: boolean = false) => {
    const baseSchema = z
        .object({
            isAddressSameAsPrimary: z.literal(false),
        })
        .merge(mailingAddressBaseSchema);

    return baseSchema.extend(hasDetailedMarriageFields ? spouseFieldsDetailed : spouseFieldsSimple);
};

const createWithoutMailingAddress = (hasDetailedMarriageFields: boolean = false) => {
    const baseSchema = z.object({
        isAddressSameAsPrimary: z.literal(true),
    });

    return baseSchema.extend(hasDetailedMarriageFields ? spouseFieldsDetailed : spouseFieldsSimple);
};

const createCoApplicant = (
    config: {
        additionalApplicantFields?: string[];
        providerCategory?: string;
        statesWithSpouseInformation?: string[];
        useAnnualIncome?: boolean;
        personalInfoFieldOverrides?: any;
        turnsOverrides?: any;
        hasDetailedMarriageFields?: boolean;
        secondLookProviderConfig?: Pick<ProviderConfig, 'fieldValueSets'>;
        emailOptional?: boolean;
    } = {},
) => {
    const hasDetailedMarriageFields = config.hasDetailedMarriageFields ?? false;
    const withMailingAddress = createWithMailingAddress(hasDetailedMarriageFields);
    const withoutMailingAddress = createWithoutMailingAddress(hasDetailedMarriageFields);

    return z.object({
        hasCoApplicant: z.literal(true),
        coApplicant: z
            .discriminatedUnion('isAddressSameAsPrimary', [
                withMailingAddress,
                withoutMailingAddress,
            ])
            .and(createApplicantSchema(config)),
    });
};

export const createSchema = (
    config: {
        additionalApplicantFields?: string[];
        providerCategory?: string;
        statesWithSpouseInformation?: string[];
        useAnnualIncome?: boolean;
        personalInfoFieldOverrides?: any;
        turnsOverrides?: any;
        hasDetailedMarriageFields?: boolean;
        primaryApplicantState?: string;
        secondLookProviderConfig?: Pick<ProviderConfig, 'fieldValueSets'>;
        emailOptional?: boolean;
    } = {},
) => {
    const hasDetailedMarriageFields = config.hasDetailedMarriageFields ?? false;
    const statesWithSpouseInformation = config.statesWithSpouseInformation ?? [];
    const baseSchema = z.discriminatedUnion(
        'hasCoApplicant',
        [noCoApplicant, createCoApplicant(config)],
        {
            errorMap: () => ({ message: 'Select yes or no' }),
        },
    );

    if (!hasDetailedMarriageFields) {
        return baseSchema.superRefine((data, ctx) => {
            if (data.hasCoApplicant) {
                const coApplicant = data.coApplicant as Parameters<
                    typeof validateSimpleMarriageFields
                >[0] & {
                    isAddressSameAsPrimary: boolean;
                    address?: { state?: string };
                };

                // Determine effective state: use primary's state when address is same,
                // otherwise use co-applicant's own state
                const effectiveState = coApplicant.isAddressSameAsPrimary
                    ? config.primaryApplicantState
                    : coApplicant.address?.state;

                if (
                    effectiveState &&
                    statesWithSpouseInformation.includes(effectiveState) &&
                    coApplicant.isMarried === undefined
                ) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Select a marital status',
                        path: ['coApplicant', 'isMarried'],
                    });
                }

                // Type assertion needed: Zod's .and() intersection infers spouseFirstName/spouseLastName
                // as unknown instead of string, but the schema has already validated these as strings
                validateSimpleMarriageFields(coApplicant, ctx, ['coApplicant']);
            }
        });
    }

    return baseSchema;
};

// Default schema for backward compatibility
export const schema = createSchema();

export type CoApplicantData = z.infer<typeof schema>;
